import type { ActionReturn } from "svelte/action"

const circumference = (r: number) => 2 * Math.PI * r
const quadrant = (r: number) => circumference(r) / 4

export const dashStyles = (
  /** closure percentage in [0,1] range */
  closure: number,
  width: number,
  height: number,
  R: number,
  /** length of the dash in "open" state */
  minLen: number,
) => {
  // the frame total length
  const total = 2 * (width + height) - 4 * (2 * R) + 4 * quadrant(R)
  // length of the dash in "closed" state
  const maxLen = total / 4
  // target dash length
  const dashLen = minLen + closure * (maxLen - minLen)

  // the dash pattern begins at the point where the top left corner curve meets the straight
  // https://stackoverflow.com/a/41478413
  const offsetLen = width - 2 * R
  // middle of top-right corner
  const offsetMin = offsetLen + quadrant(R) / 2
  // middle of the frame top border
  const offsetMax = offsetLen / 2

  const offset = offsetMin - dashLen / 2
  const dashOffset = Math.max(offset, offsetMax)
  const overflow = Math.max(0, dashOffset - offset)

  // horizontal gap
  const xGap = width - 2 * R + quadrant(R) - dashLen + 2 * overflow
  // vertical gap
  const yGap = height - 2 * R + quadrant(R) - dashLen - 2 * overflow

  return {
    strokeDasharray: [
      dashLen,
      // With custom radius gaps sizes may be very close to `0` but not equal to it. Rounding these values to `0` as this may result in incorrect closure transition.
      Math.abs(yGap) < 0.01 ? 0 : yGap,
      dashLen,
      Math.abs(xGap) < 0.01 ? 0 : xGap,
    ].join(" "),
    strokeDashoffset: `-${dashOffset}`,
  }
}

const getParentHTMLElement = (element: Element) => {
  let el = element.parentElement as HTMLElement | SVGElement | null

  while (el && !(el instanceof HTMLElement)) el = el?.parentElement ?? null

  return el
}

export const stroke = (rect: SVGRectElement, initialValue: number): ActionReturn<number> => {
  const style = getComputedStyle(rect)
  const rx = parseFloat(style.getPropertyValue("rx"))
  const baseWidth = parseFloat(style.getPropertyValue("width"))
  const minLength = parseFloat(style.getPropertyValue("stroke-dasharray"))

  let width = 0
  let height = 0

  let value = initialValue

  const apply = (closure: number) => {
    Object.assign(rect.style, dashStyles(closure, width, height, rx, minLength))
  }

  apply(value)

  let observer: ResizeObserver

  // workaround for histoire‘s jsdom
  if (typeof ResizeObserver !== "undefined") {
    observer = new ResizeObserver(() => {
      const cr = rect.getBoundingClientRect()

      width = cr.width
      height = cr.height

      if (width === 0 || height === 0) return

      const scale = baseWidth / width

      rect.style.setProperty("rx", `${scale * rx}`)

      apply(value)
    })

    // Observe the nearest HTML element, Safari does not propagate SVG element size changes
    observer.observe(getParentHTMLElement(rect)!)
  }

  return {
    update: (val = 0) => apply((value = val)),
    destroy: () => observer?.disconnect(),
  }
}
